import type { BrowserSqliteOpenConfig } from './browserSqliteProtocol'
import type {
    ConnectionDiag,
    ConnectionOptions,
    DatabaseDiagnostics,
    ExecResult,
    PreparedStatement,
    QueryOptions,
    SqlParam,
    SqliteCloseOptions,
    SqliteConnection,
    SqliteDatabase,
    SqliteFlushLocalOptions,
    SqliteStatementResult,
    SqliteTransaction,
    SyncEvent,
    SyncMode,
    SyncResult,
    TransactionOptions,
} from './sqliteDatabase'
import { SqliteError } from './sqliteDatabase'
import type { Sqlite3MtLightReconnectOptions } from './sqliteEngine'
import type { WorkspaceAuthProvider } from './workspaceSecretV2'
import { createWorkspaceAuthProvider } from './workspaceAuth'

const WS_OPEN = 1
const DEFAULT_REQUEST_TIMEOUT_MS = 300_000
const DEFAULT_RECONNECT_INITIAL_DELAY_MS = 250
const DEFAULT_RECONNECT_MAX_DELAY_MS = 5_000
const DEFAULT_RECONNECT_JITTER = 0.2

type WireParam =
    | string
    | number
    | boolean
    | null
    | { type: 'bytes'; base64: string }
    | { type: 'bigint'; value: string }

interface PendingRequest {
    resolve(value: unknown): void
    reject(reason: unknown): void
    timeout: ReturnType<typeof setTimeout>
}

interface WireError {
    type: 'error'
    requestId?: number
    code: string
    message: string
    sqliteCode?: number
    sqliteCodeName?: string
}

interface WireResult<T = Record<string, unknown>> {
    type: 'result'
    requestId: number
    rows: T[]
    changes: number
    lastInsertRowId: string
}

interface WireOpenResult {
    type: 'openResult'
    requestId: number
    ok: true
    expiresAt: string
}

interface ReconnectPolicy {
    enabled: boolean
    initialDelayMs: number
    maxDelayMs: number
    jitterRatio: number
    maxAttempts: number
}

class Sqlite3MtLightProtocolError extends Error {
    constructor(readonly code: string, message: string) {
        super(`${code}: ${message}`)
        this.name = 'Sqlite3MtLightProtocolError'
    }
}

export async function openSqlite3MtLightDatabase(config: BrowserSqliteOpenConfig): Promise<SqliteDatabase> {
    const authProvider = createWorkspaceAuthProvider(config.apiBaseUrl, config.workspaceSecret, config.blobId)
    if (!authProvider) {
        throw new Error('sqlite3mtlight requires workspace-secret-v2 auth configuration.')
    }

    return new Sqlite3MtLightDatabase(config, authProvider)
}

class Sqlite3MtLightDatabase implements SqliteDatabase {
    private readonly connections = new Set<Sqlite3MtLightConnection>()
    private readonly syncListeners = new Set<(event: SyncEvent) => void>()
    private _isOpen = true
    private _syncMode: SyncMode = 'concurrent' as SyncMode
    private readonly workspaceName: string

    constructor(
        private readonly config: BrowserSqliteOpenConfig,
        private readonly authProvider: WorkspaceAuthProvider,
    ) {
        this.workspaceName = `${config.blobId}:${config.path}`
    }

    get syncMode(): SyncMode { return this._syncMode }
    get isOpen(): boolean { return this._isOpen }
    get connectionCount(): number {
        return [...this.connections].filter(connection => connection.isOpen).length
    }

    async connect(options?: ConnectionOptions): Promise<SqliteConnection> {
        this.assertOpen()
        const connection = new Sqlite3MtLightConnection(
            this.config,
            this.authProvider,
            options?.label ?? `sqlite3mtlight-${this.connections.size + 1}`,
            () => this.connections.delete(connection),
        )
        this.connections.add(connection)
        await connection.open()
        return connection
    }

    async flushLocal(_options?: SqliteFlushLocalOptions): Promise<void> {}
    async flushRemote(): Promise<void> {}

    async close(_options?: SqliteCloseOptions): Promise<void> {
        if (!this._isOpen) return
        this._isOpen = false
        await Promise.allSettled([...this.connections].map(connection => connection.close()))
        this.connections.clear()
    }

    async setSyncMode(mode: SyncMode): Promise<void> {
        const previous = this._syncMode
        this._syncMode = mode
        if (previous !== mode) {
            this.emitSync({ type: 'mode-change', from: previous, to: mode, reason: 'sqlite3mtlight-option' })
        }
    }

    async startSync(_options?: { interval?: number }): Promise<void> {}
    async stopSync(): Promise<void> {}

    async syncNow(): Promise<SyncResult> {
        return { direction: 'no-op', pagesChanged: 0, conflicts: [], durationMs: 0 }
    }

    onSync(callback: (event: SyncEvent) => void): () => void {
        this.syncListeners.add(callback)
        return () => this.syncListeners.delete(callback)
    }

    setDebugStageListener(_listener: ((payload: unknown) => void) | null): void {}

    diagnostics(): DatabaseDiagnostics {
        return emptyDiagnostics(
            this.workspaceName,
            [...this.connections].map(connection => connection.diagnostics()),
            this._syncMode,
        )
    }

    onDiagnostics(_callback: (diag: DatabaseDiagnostics) => void): () => void {
        return () => {}
    }

    async [Symbol.asyncDispose](): Promise<void> {
        await this.close()
    }

    private assertOpen(): void {
        if (!this._isOpen) throw new Error('sqlite3mtlight database is closed.')
    }

    private emitSync(event: SyncEvent): void {
        for (const listener of this.syncListeners) {
            try { listener(event) } catch { /* listener errors are isolated */ }
        }
    }
}

class Sqlite3MtLightConnection implements SqliteConnection {
    private socket: WebSocket | null = null
    private connectPromise: Promise<void> | null = null
    private nextRequestId = 1
    private pending = new Map<number, PendingRequest>()
    private operationTail: Promise<void> = Promise.resolve()
    private _isOpen = true
    private closing = false
    private inTransaction = false
    private transactionType: 'deferred' | 'immediate' | undefined
    private diag: ConnectionDiag

    constructor(
        private readonly config: BrowserSqliteOpenConfig,
        private readonly authProvider: WorkspaceAuthProvider,
        label: string,
        private readonly onClosed: () => void,
    ) {
        this.diag = {
            label,
            isOpen: true,
            inTransaction: false,
            preparedStatementCount: 0,
            lastOperationMs: 0,
            totalQueries: 0,
            totalWrites: 0,
        }
    }

    get isOpen(): boolean { return this._isOpen }
    get pageCount(): number { return 0 }

    async open(): Promise<void> {
        await this.ensureSocket(true)
    }

    diagnostics(): ConnectionDiag {
        return this.diag
    }

    async execute<T = Record<string, unknown>>(
        sql: string,
        params?: SqlParam[],
        options?: QueryOptions,
    ): Promise<SqliteStatementResult<T>> {
        return this.enqueue(async () => {
            options?.signal?.throwIfAborted()
            return this.executeDirect<T>(sql, params, true)
        })
    }

    async query<T = Record<string, unknown>>(sql: string, params?: SqlParam[], options?: QueryOptions): Promise<T[]> {
        return (await this.execute<T>(sql, params, options)).rows
    }

    async exec(sql: string, params?: SqlParam[], options?: QueryOptions): Promise<ExecResult> {
        const result = await this.execute(sql, params, options)
        return { changes: result.changes, lastInsertRowId: result.lastInsertRowId }
    }

    async *each<T = Record<string, unknown>>(sql: string, params?: SqlParam[], options?: QueryOptions): AsyncIterable<T> {
        const rows = await this.query<T>(sql, params, options)
        for (const row of rows) yield row
    }

    async prepare<T = Record<string, unknown>>(sql: string): Promise<PreparedStatement<T>> {
        let finalized = false
        const ensureOpen = () => {
            if (finalized) throw new Error('Statement already finalized')
        }
        return {
            all: async (params?: SqlParam[]) => {
                ensureOpen()
                return this.query<T>(sql, params)
            },
            get: async (params?: SqlParam[]) => {
                ensureOpen()
                return (await this.query<T>(sql, params))[0] ?? null
            },
            run: async (params?: SqlParam[]) => {
                ensureOpen()
                return this.exec(sql, params)
            },
            finalize: async () => { finalized = true },
            [Symbol.asyncDispose]: async () => { finalized = true },
        }
    }

    async transaction<T>(
        fn: (tx: SqliteTransaction) => Promise<T>,
        options?: TransactionOptions,
    ): Promise<T> {
        return this.enqueue(async () => {
            if (this.inTransaction) throw new Error('SQLite transaction is already active on this connection.')
            await this.executeDirect(options?.immediate ? 'BEGIN IMMEDIATE' : 'BEGIN DEFERRED', undefined, true)
            this.inTransaction = true
            this.transactionType = options?.immediate ? 'immediate' : 'deferred'
            this.updateDiag({ inTransaction: true, transactionType: this.transactionType })

            const tx: SqliteTransaction = {
                execute: <T2 = Record<string, unknown>>(sql: string, params?: SqlParam[]) =>
                    this.executeDirect<T2>(sql, params, false),
                query: async <T2 = Record<string, unknown>>(sql: string, params?: SqlParam[]) =>
                    (await tx.execute<T2>(sql, params)).rows,
                exec: async (sql: string, params?: SqlParam[]) => {
                    const result = await tx.execute(sql, params)
                    return { changes: result.changes, lastInsertRowId: result.lastInsertRowId }
                },
                each: async function* <T2 = Record<string, unknown>>(sql: string, params?: SqlParam[]) {
                    const rows = await tx.query<T2>(sql, params)
                    for (const row of rows) yield row
                },
            }

            try {
                const result = await fn(tx)
                await this.executeDirect('COMMIT', undefined, false)
                return result
            } catch (error) {
                try {
                    await this.executeDirect('ROLLBACK', undefined, false)
                } catch {
                    // The server-side SQLite connection may already be gone.
                }
                throw error
            } finally {
                this.inTransaction = false
                this.transactionType = undefined
                this.updateDiag({ inTransaction: false, transactionType: undefined })
            }
        })
    }

    async close(): Promise<void> {
        if (!this._isOpen) return
        this._isOpen = false
        this.closing = true
        try {
            if (this.socket?.readyState === WS_OPEN) {
                try {
                    await this.requestOnCurrentSocket({ type: 'close' })
                } catch {
                    // best-effort close
                }
                this.socket.close()
            }
        } finally {
            this.rejectPending(new Error('sqlite3mtlight connection closed.'))
            this.socket = null
            this.updateDiag({ isOpen: false, inTransaction: false, transactionType: undefined })
            this.onClosed()
        }
    }

    async [Symbol.asyncDispose](): Promise<void> {
        await this.close()
    }

    private async executeDirect<T>(
        sql: string,
        params: SqlParam[] | undefined,
        allowReconnect: boolean,
    ): Promise<SqliteStatementResult<T>> {
        if (!this._isOpen) throw new Error('sqlite3mtlight connection is closed.')
        const startedAt = performance.now()
        const response = await this.request<WireResult<T>>({
            type: 'execute',
            sql,
            params: params?.map(encodeParam),
        }, allowReconnect)
        const result = {
            rows: response.rows.map(row => decodeRow(row)) as T[],
            changes: response.changes,
            lastInsertRowId: BigInt(response.lastInsertRowId),
        }
        this.updateDiag({
            lastOperationMs: Math.max(0, performance.now() - startedAt),
            totalQueries: this.diag.totalQueries + 1,
            totalWrites: this.diag.totalWrites + (result.changes > 0 ? 1 : 0),
        })
        return result
    }

    private enqueue<T>(operation: () => Promise<T>): Promise<T> {
        const run = this.operationTail.then(operation, operation)
        this.operationTail = run.then(() => undefined, () => undefined)
        return run
    }

    private async request<T>(payload: Record<string, unknown>, allowReconnect: boolean): Promise<T> {
        await this.ensureSocket(allowReconnect)
        return this.requestOnCurrentSocket<T>(payload)
    }

    private async requestOnCurrentSocket<T>(payload: Record<string, unknown>): Promise<T> {
        const socket = this.socket
        if (!socket || socket.readyState !== WS_OPEN) {
            throw new Error('sqlite3mtlight websocket is not open.')
        }

        const requestId = this.nextRequestId++
        const timeoutMs = Math.max(1, this.config.useSqlite3Mt?.sqlite3MtLight?.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS)
        const promise = new Promise<T>((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.pending.delete(requestId)
                reject(new Error(`sqlite3mtlight request timed out after ${timeoutMs}ms.`))
            }, timeoutMs)
            this.pending.set(requestId, {
                resolve: resolve as (value: unknown) => void,
                reject,
                timeout,
            })
        })

        socket.send(JSON.stringify({ ...payload, requestId }))
        return promise
    }

    private async ensureSocket(allowReconnect: boolean): Promise<void> {
        if (this.socket?.readyState === WS_OPEN) return
        if (!allowReconnect) throw new Error('sqlite3mtlight websocket closed during active transaction.')
        if (this.connectPromise) return this.connectPromise
        this.connectPromise = this.connectWithBackoff().finally(() => {
            this.connectPromise = null
        })
        return this.connectPromise
    }

    private async connectWithBackoff(): Promise<void> {
        const policy = resolveReconnectPolicy(this.config)
        let attempt = 0
        while (true) {
            try {
                await this.connectOnce()
                return
            } catch (error) {
                if (!policy.enabled || !isRetryableConnectError(error) || attempt >= policy.maxAttempts) throw error
                const delayMs = computeDelayMs(policy, attempt)
                attempt++
                await delay(delayMs)
            }
        }
    }

    private async connectOnce(): Promise<void> {
        const WebSocketCtor = globalThis.WebSocket
        if (!WebSocketCtor) throw new Error('sqlite3mtlight requires global WebSocket support.')

        const socket = new WebSocketCtor(resolveWebSocketUrl(this.config))
        this.socket = socket
        socket.onmessage = event => {
            void this.handleMessage(event.data)
        }
        socket.onclose = () => this.handleSocketClosed()

        try {
            await waitForOpen(socket)

            const token = await this.authProvider.getAccessToken('read')
            await this.requestOnCurrentSocket<WireOpenResult>({
                type: 'open',
                blobId: this.config.blobId,
                path: this.config.path,
                accessToken: token.token,
            })
        } catch (error) {
            if (this.socket === socket) {
                this.socket = null
            }
            try {
                socket.close()
            } catch {
                // best-effort cleanup of a failed open attempt
            }
            throw error
        }
    }

    private async handleMessage(data: unknown): Promise<void> {
        let payload: any
        try {
            payload = JSON.parse(await messageDataToString(data))
        } catch (error) {
            this.rejectPending(error)
            return
        }

        const requestId = typeof payload.requestId === 'number' ? payload.requestId : null
        if (requestId == null) return

        const pending = this.pending.get(requestId)
        if (!pending) return
        clearTimeout(pending.timeout)
        this.pending.delete(requestId)

        if (payload.type === 'error') {
            pending.reject(wireErrorToError(payload as WireError))
            return
        }

        pending.resolve(payload)
    }

    private handleSocketClosed(): void {
        this.socket = null
        this.rejectPending(new Error('sqlite3mtlight websocket closed.'))
        if (this.inTransaction) {
            this.inTransaction = false
            this.transactionType = undefined
            this.updateDiag({ inTransaction: false, transactionType: undefined })
        }
    }

    private rejectPending(reason: unknown): void {
        for (const pending of this.pending.values()) {
            clearTimeout(pending.timeout)
            pending.reject(reason)
        }
        this.pending.clear()
    }

    private updateDiag(partial: Partial<ConnectionDiag>): void {
        this.diag = { ...this.diag, ...partial }
    }
}

function resolveWebSocketUrl(config: BrowserSqliteOpenConfig): string {
    const override = config.useSqlite3Mt?.sqlite3MtLight?.websocketUrl
    if (override) return override

    const url = new URL(config.apiBaseUrl)
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
    url.pathname = `${url.pathname.replace(/\/+$/, '')}/sqlite3mtlight/ws`
    url.search = ''
    url.hash = ''
    return url.toString()
}

function resolveReconnectPolicy(config: BrowserSqliteOpenConfig): ReconnectPolicy {
    const value = config.useSqlite3Mt?.sqlite3MtLight?.reconnect
    if (value === false) {
        return { enabled: false, initialDelayMs: 0, maxDelayMs: 0, jitterRatio: 0, maxAttempts: 0 }
    }
    const options: Sqlite3MtLightReconnectOptions = typeof value === 'object' && value !== null ? value : {}
    return {
        enabled: true,
        initialDelayMs: options.initialDelayMs ?? DEFAULT_RECONNECT_INITIAL_DELAY_MS,
        maxDelayMs: options.maxDelayMs ?? DEFAULT_RECONNECT_MAX_DELAY_MS,
        jitterRatio: options.jitterRatio ?? DEFAULT_RECONNECT_JITTER,
        maxAttempts: options.maxAttempts ?? Number.POSITIVE_INFINITY,
    }
}

function computeDelayMs(policy: ReconnectPolicy, attempt: number): number {
    const base = Math.min(policy.maxDelayMs, policy.initialDelayMs * (2 ** attempt))
    const jitter = base * policy.jitterRatio
    return Math.max(0, Math.round(base - jitter + Math.random() * jitter * 2))
}

function delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
}

function waitForOpen(socket: WebSocket): Promise<void> {
    if (socket.readyState === WS_OPEN) return Promise.resolve()
    return new Promise((resolve, reject) => {
        socket.onopen = () => resolve()
        socket.onerror = () => reject(new Error('sqlite3mtlight websocket open failed.'))
    })
}

function encodeParam(param: SqlParam): WireParam {
    if (param instanceof Uint8Array) return { type: 'bytes', base64: bytesToBase64(param) }
    if (typeof param === 'bigint') return { type: 'bigint', value: param.toString() }
    return param
}

function decodeRow<T>(row: T): T {
    if (row == null || typeof row !== 'object') return row
    const decoded: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(row as Record<string, unknown>)) {
        decoded[key] = decodeValue(value)
    }
    return decoded as T
}

function decodeValue(value: unknown): unknown {
    if (value && typeof value === 'object') {
        const typed = value as { type?: unknown; base64?: unknown; value?: unknown }
        if (typed.type === 'bytes' && typeof typed.base64 === 'string') {
            return base64ToBytes(typed.base64)
        }
        if (typed.type === 'bigint' && typeof typed.value === 'string') {
            return BigInt(typed.value)
        }
    }
    return value
}

function wireErrorToError(error: WireError): Error {
    if (typeof error.sqliteCode === 'number') {
        return new SqliteError(error.message, error.sqliteCode, error.sqliteCodeName ?? error.code)
    }
    return new Sqlite3MtLightProtocolError(error.code, error.message)
}

function isRetryableConnectError(error: unknown): boolean {
    if (error instanceof Sqlite3MtLightProtocolError || error instanceof SqliteError) {
        return false
    }

    const message = error instanceof Error ? error.message : String(error)
    if (/^Auth challenge failed: (400|401|403|404)\b/.test(message)) {
        return false
    }
    if (message.includes('workspace secret blobId') || message.includes('has no writerSeed')) {
        return false
    }

    return true
}

async function messageDataToString(data: unknown): Promise<string> {
    if (typeof data === 'string') return data
    if (data instanceof ArrayBuffer) return new TextDecoder().decode(data)
    if (data instanceof Uint8Array) return new TextDecoder().decode(data)
    if (typeof Blob !== 'undefined' && data instanceof Blob) return data.text()
    return String(data)
}

function bytesToBase64(bytes: Uint8Array): string {
    if (typeof Buffer !== 'undefined') return Buffer.from(bytes).toString('base64')
    let binary = ''
    for (let i = 0; i < bytes.length; i += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
    }
    return btoa(binary)
}

function base64ToBytes(base64: string): Uint8Array {
    if (typeof Buffer !== 'undefined') return new Uint8Array(Buffer.from(base64, 'base64'))
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return bytes
}

function emptyDiagnostics(
    workspace: string,
    connections: readonly ConnectionDiag[],
    mode: SyncMode,
): DatabaseDiagnostics {
    return {
        timestamp: Date.now(),
        workspace,
        memory: {
            sab1Size: 0,
            sab2Size: 0,
            sab3Size: 0,
            totalSabBytes: 0,
            pageWindowSlots: 0,
            pageWindowUsed: 0,
            dirtyPageCount: 0,
            pageSize: 0,
            dbPageCount: 0,
            sabSupported: typeof SharedArrayBuffer !== 'undefined',
        },
        wal: {
            mxFrame: 0,
            walDataSize: 0,
            frameCount: 0,
            lastCheckpointFrame: 0,
            framesSinceCheckpoint: 0,
            autoCheckpointThreshold: 0,
        },
        connections,
        sync: {
            mode,
            state: 'idle',
            isSyncing: false,
            cycleCount: 0,
            lastResult: null,
            lastError: null,
            lastSyncAt: null,
            nextSyncAt: null,
            pendingPushPages: 0,
            remoteEpoch: 0,
            localEpoch: 0,
            casRetryCount: 0,
            lastConflictCount: 0,
            leaseId: null,
            leaseTtlRemainingMs: null,
        },
        locks: {
            writeLock: 0,
            checkpointLock: 0,
            recoverLock: 0,
            readLocks: [0, 0, 0, 0, 0],
            isWriteLocked: false,
            importPending: false,
        },
        drain: {
            mode0Active: false,
            drainPending: false,
            transactionsBlocked: false,
            initiator: null,
            drainStartedAt: null,
            drainTimeoutAt: null,
        },
    }
}
